/**
 * Form Data Transformers for Step 3
 * 
 * Handles transformation between form state, store data, and AI responses
 * Adapted for React Hook Form (JobPostFormData)
 */

import { JobPostFormData } from '../../../schemas/jobPostValidation';
import { JobDescriptionEntity } from '../../../../../Entities/JobDescription';
import { InterviewRoundType } from '../../../../../Dto/GenerateJobDescription.dto';

/**
 * Local state type for interview rounds (used in component)
 */
export interface InterviewRoundState {
  interviewRoundId: string | null;
  interviewRoundNumber: number;
  isMustQualify: boolean;
  passPercentage?: number; // Per-round pass percentage
  title?: string; // For display purposes only
  _id?: string;   // For newly created rounds or existing rounds
  description?: string; // For display purposes
  questionGenerationPrompt?: string; // For display purposes
  evaluationPrompt?: string; // For display purposes
  answerType?: string; // Question type (text, multiple_choice, code)
  category?: string[]; // Category of the interview round
  _fetchedAt?: number; // Timestamp when metadata was fetched
  _version?: number; // Version for cache invalidation
  _isNewlyAdded?: boolean; // Flag to track rounds added during edit mode (allows editing/deleting)
}

/**
 * Transform interview rounds array to Step3Data (form format)
 * Preserves display metadata
 */
export const transformFormToStep3 = (
  rounds: InterviewRoundState[],
  isProfileMatchRequired: boolean = true,
  interviewRoundExpiry?: number
): JobPostFormData['step3'] => {
  const transformedRounds = rounds.map((round) => ({
    interviewRoundId: round.interviewRoundId ?? round._id ?? null,
    interviewRoundNumber: round.interviewRoundNumber,
    isMustQualify: round.isMustQualify ?? true,
    passPercentage: round.passPercentage,
    // Store display metadata
    _displayMetadata: {
      title: round.title,
      description: round.description,
      questionGenerationPrompt: round.questionGenerationPrompt,
      evaluationPrompt: round.evaluationPrompt,
      answerType: round.answerType,
      category: round.category,
      _id: round._id,
      _version: round._version,
      _fetchedAt: round._fetchedAt,
      _isNewlyAdded: round._isNewlyAdded, // Preserve newly added flag
    },
  }));

  return {
    interviewRounds: transformedRounds,
    isProfileMatchRequired,
    interviewRoundExpiry: interviewRoundExpiry ?? 5,
  };
};

/**
 * Transform Step3Data to local state (for component)
 * Preserves display metadata if available
 * Filters out Job Alignment rounds (they belong to Step 4, not Step 3)
 */
export const transformStoreToStep3 = (step3Data: JobPostFormData['step3'] | null | undefined): InterviewRoundState[] => {
  if (!step3Data?.interviewRounds) {
    return [];
  }

  // Filter out Job Alignment / Requirements rounds (they belong to Step 4)
  const filteredRounds = step3Data.interviewRounds.filter((round) => {
    const id = round.interviewRoundId || '';
    const metadata = (round as any)._displayMetadata ?? {};
    const title = metadata.title?.toLowerCase() || '';
    const isJobAlignment = id === 'job-alignment' || 
                          title === 'job alignment' ||
                          title === 'requirements' ||
                          (metadata.category && Array.isArray(metadata.category) && 
                           metadata.category.some((cat: string) => 
                             cat.toLowerCase().replace(/\s+/g, '') === 'jobalignment'
                           ));
    return !isJobAlignment;
  });

  return filteredRounds.map((round) => {
    const metadata = (round as any)._displayMetadata ?? {};
    // Use interviewRoundId as _id if _id is not available (for rounds loaded from edit mode)
    const roundId = metadata._id ?? round.interviewRoundId ?? undefined;
    return {
      interviewRoundId: round.interviewRoundId,
      interviewRoundNumber: round.interviewRoundNumber,
      isMustQualify: round.isMustQualify ?? true,
      passPercentage: round.passPercentage,
      _id: roundId,
      title: metadata.title,
      description: metadata.description,
      questionGenerationPrompt: metadata.questionGenerationPrompt,
      evaluationPrompt: metadata.evaluationPrompt,
      answerType: metadata.answerType,
      category: metadata.category,
      _fetchedAt: metadata._fetchedAt,
      _version: metadata._version,
      _isNewlyAdded: metadata._isNewlyAdded, // Preserve newly added flag
    };
  });
};

/**
 * Map AI-generated JobDescriptionEntity to Step3Data
 * Used when AI generates interview rounds in Step 0
 */
export const mapJobDescriptionToStep3 = (jobPost: JobDescriptionEntity | null): JobPostFormData['step3'] => {
  if (!jobPost?.interviewRounds) {
    return {
      interviewRounds: [],
      isProfileMatchRequired: jobPost?.isProfileMatchRequired ?? true,
      interviewRoundExpiry: jobPost?.interviewRoundExpiry ?? 5,
    };
  }

  // Filter out Job Alignment from Step 3 (Job Alignment is now in Step 4)
  const interviewRounds: InterviewRoundType[] = (jobPost.interviewRounds || [])
    .filter((round) => {
      const id = round.interviewRoundId || '';
      return id !== 'job-alignment';
    })
    .map((round) => ({
      interviewRoundId: round.interviewRoundId || null,
      interviewRoundNumber: round.interviewRoundNumber || 1,
      isMustQualify: round.isMustQualify ?? true,
      passPercentage: round.passPercentage, // Include pass percentage if available
      isAIGenerated: round.isAIGenerated ?? true, // Include isAIGenerated
    }));

  return {
    interviewRounds,
    isProfileMatchRequired: jobPost.isProfileMatchRequired ?? true,
    interviewRoundExpiry: jobPost.interviewRoundExpiry ?? 5,
  };
};

/**
 * Map AI response to Step3Data format
 * Used when AI refines Step 3 interview rounds via ?step=interview-rounds
 */
export const mapAIResponseToStep3Data = (aiResponse: any): JobPostFormData['step3'] => {
  if (!aiResponse) {
    return {
      interviewRounds: [],
      isProfileMatchRequired: true,
      interviewRoundExpiry: 5,
    };
  }

  // AI may return interviewRounds in different formats
  const interviewRounds: InterviewRoundType[] = (aiResponse.interviewRounds || []).map((round: any, index: number) => ({
    interviewRoundId: round.interviewRoundId || round._id || round.id || null,
    interviewRoundNumber: round.interviewRoundNumber || round.roundNumber || index + 1,
    isMustQualify: round.isMustQualify ?? true,
    passPercentage: round.passPercentage,
  }));

  return {
    interviewRounds,
    isProfileMatchRequired: true,
    interviewRoundExpiry: aiResponse.interviewRoundExpiry ?? 5,
  };
};

/**
 * Merge AI-generated data with existing user data
 * Preserves user modifications, only updates AI-generated fields
 */
export const mergeAIWithUserData = (
  userData: JobPostFormData['step3'] | null,
  aiData: JobPostFormData['step3']
): JobPostFormData['step3'] => {
  if (!userData) {
    return aiData;
  }

  // For interview rounds, preserve user's rounds but merge any AI enhancements
  // User's rounds take precedence
  return {
    interviewRounds: userData.interviewRounds.length > 0
      ? userData.interviewRounds
      : aiData.interviewRounds,
    isProfileMatchRequired: userData.isProfileMatchRequired ?? aiData.isProfileMatchRequired ?? true,
    interviewRoundExpiry: userData.interviewRoundExpiry ?? aiData.interviewRoundExpiry ?? 5,
  };
};


